import React from 'react';
import { AnalyticsMetrics } from '../../types';
import { 
  Activity, 
  Users, 
  Globe, 
  Clock, 
  AlertTriangle, 
  TrendingUp 
} from 'lucide-react';

interface MetricsOverviewProps {
  metrics: AnalyticsMetrics['overview'];
  isLoading?: boolean;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ComponentType<{ className?: string }>;
  trend?: 'up' | 'down' | 'stable';
  suffix?: string;
  loading?: boolean;
}

function MetricCard({ title, value, change, icon: Icon, trend, suffix = '', loading }: MetricCardProps) {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  const formatValue = (val: string | number): string => {
    if (typeof val === 'number') {
      if (val >= 1000000) {
        return `${(val / 1000000).toFixed(1)}M`;
      }
      if (val >= 1000) {
        return `${(val / 1000).toFixed(1)}K`;
      }
      return val.toFixed(suffix.includes('%') ? 1 : 0);
    }
    return val;
  };

  const getTrendColor = () => {
    if (!trend || !change) return '';
    if (trend === 'up') return 'text-green-600 dark:text-green-400';
    if (trend === 'down') return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getTrendIcon = () => {
    if (!trend || !change) return null;
    return (
      <TrendingUp 
        className={`w-4 h-4 ${getTrendColor()} ${trend === 'down' ? 'rotate-180' : ''}`}
      />
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="ml-3 sm:ml-5 w-0 flex-1">
          <dl>
            <dt className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
              {title}
            </dt>
            <dd className="flex items-baseline">
              <div className="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-white">
                {formatValue(value)}{suffix}
              </div>
              {change !== undefined && (
                <div className={`ml-2 flex items-baseline text-xs sm:text-sm font-semibold ${getTrendColor()}`}>
                  {getTrendIcon()}
                  <span className="sr-only">
                    {trend === 'up' ? 'Increased' : trend === 'down' ? 'Decreased' : 'Unchanged'} by
                  </span>
                  {Math.abs(change).toFixed(1)}%
                </div>
              )}
            </dd>
          </dl>
        </div>
      </div>
    </div>
  );
}

export function MetricsOverview({ metrics, isLoading }: MetricsOverviewProps) {
  const metricCards = [
    {
      title: 'Total Connections',
      value: metrics?.totalConnections || 0,
      icon: Globe,
      change: 12.5,
      trend: 'up' as const,
    },
    {
      title: 'Active Connections',
      value: metrics?.activeConnections || 0,
      icon: Activity,
      change: 8.2,
      trend: 'up' as const,
    },
    {
      title: 'Total Requests',
      value: metrics?.totalRequests || 0,
      icon: Users,
      change: -2.1,
      trend: 'down' as const,
    },
    {
      title: 'Avg Response Time',
      value: metrics?.averageResponseTime || 0,
      icon: Clock,
      change: 5.4,
      trend: 'down' as const,
      suffix: 'ms',
    },
    {
      title: 'Error Rate',
      value: metrics?.errorRate || 0,
      icon: AlertTriangle,
      change: -15.3,
      trend: 'down' as const,
      suffix: '%',
    },
    {
      title: 'Uptime',
      value: metrics?.uptime || 0,
      icon: TrendingUp,
      change: 0.1,
      trend: 'up' as const,
      suffix: '%',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {metricCards.map((metric) => (
        <MetricCard
          key={metric.title}
          title={metric.title}
          value={metric.value}
          change={metric.change}
          icon={metric.icon}
          trend={metric.trend}
          suffix={metric.suffix}
          loading={isLoading}
        />
      ))}
    </div>
  );
}